import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/pages/home/components/VideoCard';
import { videoCategories, videos, siteInfo } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = videoCategories.find((c) => c.slug === slug);
  const categoryName = category?.name || '全部视频';
  const filteredVideos = slug
    ? videos.filter((v) => v.categorySlug === slug)
    : videos;

  const pageUrl = `${siteInfo.url}/category/${slug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: categoryName, item: pageUrl },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - ${siteInfo.name}`,
    description: `${siteInfo.name}${categoryName}频道，聚合红桃视频、樱桃视频、茄子视频、糖心视频等平台${categoryName}资源，高清免费在线观看，实时更新。`,
    url: pageUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100/50 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <span className="text-foreground-300">/</span>
                <span className="text-foreground-900 font-medium">{categoryName}</span>
              </nav>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                <i className={`${category?.icon || 'ri-folder-line'} text-xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">{categoryName}</h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 <strong className="text-primary-600">{filteredVideos.length}</strong> 部作品
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {videoCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-base`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无内容</p>
              </div>
            )}
          </div>

          <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100/50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-fire-line text-lg text-accent-600"></i>
                </div>
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">热门推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {videos.filter((v) => v.isHot).slice(0, 6).map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}